"use client";

import { SafeHydrate } from "@/SafeHydrate";
import React, { useEffect, useState } from "react";
import { ChevronLeft, ChevronRight, CircleCheck, MessageCircleQuestionMark, Star } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Swiper, SwiperSlide, useSwiper } from "swiper/react";
import { A11y, Autoplay, Pagination } from "swiper/modules";
import "swiper/css";
import "swiper/css/pagination";
import "swiper/css/autoplay";
import "swiper/css/a11y";
const testimonials = [{
  author: "Mario R.",
  position: "Paziente da Roma",
  quote: "Ottimo lavoro. Lo staff parla perfettamente italiano. Consiglio vivamente!",
  avatar: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1663456/img/avatar_mario_r.png",
  rating: 5
}, {
  author: "Luisa P.",
  position: "Paziente da Milano",
  quote: "Intervento rapido, personale professionale, risultato naturale. Avevo molta paura prima di partire, ma l'assistenza continua mi ha rassicurata. I materiali usati sono certificati e mi è stato rilasciato il passaporto implantare.",
  avatar: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1663456/img/avatar_luisa_p.png",
  rating: 5
}, {
  author: "Giovanni T.",
  position: "Paziente da Napoli",
  quote: "Il pacchetto includeva anche l'alloggio e i trasferimenti, rendendo tutto zero stress. I nuovi denti sembrano i miei naturali, non potrei essere più felice della scelta fatta.",
  avatar: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1663456/img/avatar_giovanni_t.png",
  rating: 5
}, {
  author: "Elena C.",
  position: "Paziente da Firenze",
  quote: "La trasparenza sui prezzi è stata la cosa che ho apprezzato di più. Nessuna sorpresa finale. Il Dottore è stato chiarissimo nello spiegarmi ogni fase dell'intervento All-on-4. Struttura impeccabile e pulitissima.",
  avatar: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1663456/img/avatar_elena_c.png",
  rating: 5
}, {
  author: "Roberto M.",
  position: "Paziente da Torino",
  quote: "Ho riacquistato il sorriso e la capacità di masticare bene dopo anni di problemi.",
  avatar: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1663456/img/avatar_roberto_m.png",
  rating: 5
}];
const faqs = [{
  question: "Quanto costa un impianto dentale da SkyDental nel 2026?",
  answer: "Impianto da 350\u00A0€ a 800\u00A0€ secondo il sistema implantare, moncone compreso; corona 120\u00A0€ (metallo-ceramica) o 170\u00A0€ (zirconio); All-on-4 2.940\u00A0€ / 3.420\u00A0€ e All-on-6 3.640\u00A0€ / 4.120\u00A0€ per arcata. Il totale per il tuo caso è nel piano scritto entro 24 ore."
}, {
  question: "I materiali utilizzati per gli impianti sono sicuri e certificati?",
  answer: "Assolutamente sì. Le cliniche raccomandate utilizzano esclusivamente impianti di marchi leader a livello mondiale (come Nobel Biocare e Alpha Bio) provvisti di certificazione CE. Al termine delle cure viene sempre rilasciato il 'Passaporto Implantare' internazionale."
}, {
  question: "Quanto dura l'intero processo per avere i denti fissi?",
  answer: "Il processo si divide solitamente in due fasi. La prima visita (3-4 giorni) prevede l'inserimento degli impianti e l'applicazione di una protesi provvisoria fissa. Dopo 3-6 mesi (tempo necessario per l'osteointegrazione), si torna per la seconda fase (4-5 giorni) in cui viene applicata la protesi definitiva in zirconio o ceramica."
}, {
  question: "Offrite garanzie sugli impianti dentali?",
  answer: "Le condizioni di garanzia e il calendario dei controlli sono indicati per iscritto nel piano di trattamento."
}, {
  question: "Come funziona per il viaggio, l'alloggio e la lingua?",
  answer: "Le cliniche offrono pacchetti 'All-Inclusive' che comprendono l'assistenza per la prenotazione dei voli, i trasferimenti gratuiti da/per l'aeroporto e l'alloggio in hotel convenzionati vicini alla clinica. Tutto lo staff medico e di assistenza parla fluentemente italiano, garantendo una comunicazione chiara e senza incomprensioni."
}, {
  question: "Cosa succede se ho un problema una volta tornato in Italia?",
  answer: "Le cliniche albanesi hanno reti di dentisti partner in Italia per i controlli di routine o per gestire eventuali piccole emergenze post-operatorie senza dover necessariamente tornare a Tirana. Inoltre, l'assistenza telefonica è sempre attiva."
}];
function SliderControls() {
  const swiper = useSwiper();
  return <div className="relative z-10 flex items-center justify-between pt-4 px-1">
      <Button variant="outline" size="icon" onClick={() => swiper.slidePrev()} aria-label="Testimonianza precedente" className="size-8 rounded-lg border-border bg-card hover:bg-accent text-foreground hover:text-accent-foreground transition-colors cursor-pointer shadow-xs">
        <ChevronLeft className="size-4" />
      </Button>
      <Button variant="outline" size="icon" onClick={() => swiper.slideNext()} aria-label="Testimonianza successiva" className="size-8 rounded-lg border-border bg-card hover:bg-accent text-foreground hover:text-accent-foreground transition-colors cursor-pointer shadow-xs">
        <ChevronRight className="size-4" />
      </Button>
    </div>;
}
export default function DomandeFrequentiETestimonianze() {
  const [reduceMotion, setReduceMotion] = useState(false);
  useEffect(() => {
    const mq = window.matchMedia("(prefers-reduced-motion: reduce)");
    setReduceMotion(mq.matches);
    const onChange = () => setReduceMotion(mq.matches);
    mq.addEventListener("change", onChange);
    return () => mq.removeEventListener("change", onChange);
  }, []);
  return <section id="domande-frequenti-e-testimonianze" className="bg-background py-20 md:py-28">
      <div className="container mx-auto px-4 md:px-8 max-w-7xl">
        
        <div className="mb-14 text-center lg:text-left">
          <h2 className="text-3xl md:text-5xl font-serif text-foreground tracking-tight mb-4">
            Informazioni Chiare, <span className="text-primary italic">Pazienti Sicuri</span>
          </h2>
          <p className="text-base md:text-lg text-muted-foreground max-w-2xl font-sans leading-relaxed">
            Questa guida risponde ai dubbi più comuni e raccoglie le esperienze reali di chi ha già scelto l'Albania per le proprie cure dentali.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-14 items-start">
          
          {/* Testimonianze Column */}
          <div className="lg:col-span-5 flex flex-col h-full">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-2xl font-serif text-foreground flex items-center gap-2.5">
                <Star className="w-5 h-5 text-primary fill-primary" />
                Testimonianze
              </h3>
              <Badge variant="outline" className="bg-card text-muted-foreground border-border text-xs px-2.5 py-0.5 rounded-lg">
                Verificate
              </Badge>
            </div>
            
            <div className="w-full flex-grow">
              <Swiper modules={[Autoplay, Pagination, A11y]} spaceBetween={20} slidesPerView={1} loop autoplay={reduceMotion ? false : {
              delay: 5000,
              disableOnInteraction: false,
              pauseOnMouseEnter: true
            }} pagination={{
              clickable: true,
              bulletClass: "inline-block size-2 rounded-lg bg-muted-foreground/30 mx-1 cursor-pointer transition-all",
              bulletActiveClass: "bg-primary w-5"
            }} a11y={{
              prevSlideMessage: "Testimonianza precedente",
              nextSlideMessage: "Testimonianza successiva"
            }} className="w-full !pb-12">
                {testimonials.map((testimonial, i) => <SwiperSlide key={testimonial.author} data-index={i} className="h-auto">
                    <div className="bg-card rounded-2xl border border-border shadow-sm p-6 md:p-8 flex flex-col justify-between h-full min-h-[310px]">
                      <div>
                        <div className="flex items-start justify-between gap-4 mb-5">
                          <div className="flex items-center gap-3.5">
                            <SafeHydrate><Avatar className="w-12 h-12 border border-border/80 rounded-full">
                              <AvatarImage src={testimonial.avatar} alt={`Avatar di ${testimonial.author}`} data-wvc-srcset={`${testimonial.avatar}-wvc-srcset`} data-wvc-sizes={`${testimonial.avatar}-wvc-sizes`} />
                              <AvatarFallback className="bg-accent text-accent-foreground font-semibold font-sans">
                                {testimonial.author.charAt(0)}
                              </AvatarFallback>
                            </Avatar></SafeHydrate>
                            <div>
                              <p className="font-semibold font-sans text-foreground text-sm leading-snug">{testimonial.author}</p>
                              <p className="text-xs text-muted-foreground font-sans mt-0.5">{testimonial.position}</p>
                            </div>
                          </div>
                          <Badge variant="secondary" className="bg-accent text-accent-foreground text-[11px] font-medium px-2.5 py-0.5 rounded-lg border border-border/60">
                            <CircleCheck className="w-3.5 h-3.5 mr-1 text-primary shrink-0" />
                            Paziente Reale
                          </Badge>
                        </div>

                        <div className="flex items-center gap-1 mb-4" aria-label={`Valutazione: ${testimonial.rating} su 5 stelle`}>
                          {Array.from({
                        length: testimonial.rating
                      }).map((_, starIndex) => <Star key={starIndex} className="size-4 text-primary fill-primary" />)}
                        </div>

                        <p className="text-sm md:text-base text-foreground/80 font-sans leading-relaxed italic">
                          "{testimonial.quote}"
                        </p>
                      </div>
                    </div>
                  </SwiperSlide>)}
                <SliderControls />
              </Swiper>
            </div>
          </div>

          {/* FAQ Column */}
          <div className="lg:col-span-7 flex flex-col">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-10 h-10 rounded-xl bg-accent flex items-center justify-center shrink-0 text-primary">
                <MessageCircleQuestionMark className="w-5 h-5" />
              </div>
              <h3 className="text-2xl font-serif text-foreground">
                Domande Frequenti
              </h3>
            </div>

            <div className="bg-card rounded-2xl border border-border shadow-sm p-3 md:p-6">
              <Accordion type="single" collapsible className="w-full">
                {faqs.map((faq, i) => <AccordionItem key={i} data-index={i} value={`faq-${i}`} className="border-border/80 px-3 md:px-4">
                    <AccordionTrigger className="font-sans font-medium text-left text-base md:text-lg text-foreground hover:no-underline hover:text-primary transition-colors py-4.5">
                      {faq.question}
                    </AccordionTrigger>
                    <AccordionContent className="text-muted-foreground font-sans text-sm md:text-base leading-relaxed pl-4 border-l-2 border-primary/30 ml-1 mb-4 pt-1">
                      {faq.answer}
                    </AccordionContent>
                  </AccordionItem>)}
              </Accordion>
            </div>
          </div>

        </div>
      </div>
    </section>;
}